export const validate = (type: string, value: string): boolean => {
  switch (type) {
    case 'email':
      return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        value,
      );
    default:
      return false;
  }
};

/* validateCPFCNPJ moved below CNPJ helpers to satisfy linter */

export const validateCellphone = (inputCellPhone: string) => {
  const cellPhone = inputCellPhone.substring(2, 16);
  const dddCellPhone = inputCellPhone.substring(0, 2);
  return (
    dddCellPhone.length === 2 &&
    cellPhone.length === 9 &&
    cellPhone.startsWith('9')
  );
};

export const isUnprefixedAndValidCep = (cep: string) => {
  if (!cep) return false;

  const regex = /^[0-9]{2}.[0-9]{3}-000$/;
  if (cep.length > 0 && regex.test(cep.trim())) {
    return true;
  }

  return false;
};

// ---- Alphanumeric CNPJ support ----
// Character value mapping: use ASCII code minus 48 ("0" -> 0, "A" -> 17, ... "Z" -> 42)
// Input is normalized to uppercase and formatting chars are removed before processing.

export function calculateCnpjCheckDigits(base12: string): string {
  if (!base12) {
    throw new Error('Base CNPJ is required');
  }
  // Normalize: uppercase and remove any non-alphanumeric (dots, hyphens, slashes, spaces, etc.)
  const base = base12.toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (!/^[0-9A-Z]{12}$/.test(base)) {
    throw new Error(`CNPJ base ${base12} is not valid for DV calculation`);
  }

  function charValue(ch: string): number {
    const code = ch.toUpperCase().charCodeAt(0);
    const val = code - 48; // '0'->48 => 0; 'A'->65 => 17 ... 'Z'->90 => 42
    if (val < 0 || val > 42) {
      throw new Error(`Invalid character for CNPJ: ${ch}`);
    }
    return val;
  }

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let sum1 = 0;
  for (let i = 0; i < 12; i += 1) {
    sum1 += charValue(base[i]) * weights1[i];
  }
  const r1 = sum1 % 11;
  const d1 = r1 < 2 ? 0 : 11 - r1;

  const baseWithD1 = base + String(d1);
  let sum2 = 0;
  for (let i = 0; i < 13; i += 1) {
    sum2 += charValue(baseWithD1[i]) * weights2[i];
  }
  const r2 = sum2 % 11;
  const d2 = r2 < 2 ? 0 : 11 - r2;

  return `${d1}${d2}`;
}

export function isValidCNPJ(str: string): boolean {
  if (!str) return false;
  const cleaned = str
    .toUpperCase()
    .trim()
    .replace(/[^0-9A-Z]/g, '');

  if (
    cleaned.length !== 14 ||
    cleaned === '00000000000000' ||
    cleaned === '11111111111111' ||
    cleaned === '22222222222222' ||
    cleaned === '33333333333333' ||
    cleaned === '44444444444444' ||
    cleaned === '55555555555555' ||
    cleaned === '66666666666666' ||
    cleaned === '77777777777777' ||
    cleaned === '88888888888888' ||
    cleaned === '99999999999999'
  ) {
    return false;
  }

  // Expect 12 alphanumeric chars followed by 2 numeric DVs
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cleaned)) {
    return false;
  }

  const base = cleaned.substring(0, 12);
  const dvGiven = cleaned.substring(12);

  const dvCalculated = calculateCnpjCheckDigits(base);
  return dvCalculated === dvGiven;
}

export const validateCPFCNPJ = (inputCPFCNPJ: string) => {
  if (!inputCPFCNPJ) {
    return true;
  }

  let soma = 0;
  let resto;
  let i;

  const parsedCPFCNPJ = inputCPFCNPJ
    .replace(/\./g, '')
    .replace('-', '')
    .replace('/', '');

  if (parsedCPFCNPJ.length > 11) {
    // CNPJ validation
    if (parsedCPFCNPJ.length === 14) {
      return isValidCNPJ(parsedCPFCNPJ);
    }
    return false;
  }

  if (
    parsedCPFCNPJ === '00000000000' ||
    parsedCPFCNPJ === '11111111111' ||
    parsedCPFCNPJ === '22222222222' ||
    parsedCPFCNPJ === '33333333333' ||
    parsedCPFCNPJ === '44444444444' ||
    parsedCPFCNPJ === '55555555555' ||
    parsedCPFCNPJ === '66666666666' ||
    parsedCPFCNPJ === '77777777777' ||
    parsedCPFCNPJ === '88888888888' ||
    parsedCPFCNPJ === '99999999999'
  ) {
    return false;
  }
  for (i = 1; i <= 9; i += 1) {
    soma += parseInt(parsedCPFCNPJ.substring(i - 1, i), 10) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) {
    resto = 0;
  }
  if (resto !== parseInt(parsedCPFCNPJ.substring(9, 10), 10)) {
    return false;
  }
  soma = 0;
  for (i = 1; i <= 10; i += 1) {
    soma += parseInt(parsedCPFCNPJ.substring(i - 1, i), 10) * (12 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) {
    resto = 0;
  }
  if (resto !== parseInt(parsedCPFCNPJ.substring(10, 11), 10)) {
    return false;
  }
  return true;
};

export const scorePenalty = (password: string): number => {
  const regex = [/[^A-Za-z0-9]/, /[a-z]/, /[A-Z]/, /[0-9]/];

  const scoreResult = regex.reduce((score: number, validation: RegExp) => {
    if (!validation.test(password)) {
      return score + 1;
    }
    return score;
  }, 0);

  return scoreResult;
};

export const blockedCepList = [
  '11111111',
  '22222222',
  '33333333',
  '55555555',
  '66666666',
  '77777777',
  '88888888',
  '99999999',
  '12345678',
  '87654321',
];

export const isValidCep = (cep: string): boolean => {
  if (!cep) return true;

  const cepTrimmed = cep.trim();

  const isValidFormat = /^[0-9]{2}\.[0-9]{3}-[0-9]{3}$/.test(cepTrimmed);
  if (!isValidFormat) return false;

  const cepUnmasked = cepTrimmed.replace(/\D/g, '');

  if (blockedCepList.includes(cepUnmasked)) return false;

  return true;
};
